import { PlainDate, getCurrentDate } from "@sa-nu/shared-utils/date";
import { parsePlainDate } from "@sa-nu/shared-utils/date";
import { formatCurrency } from "@sa-nu/shared-utils/price";
import clsx from "clsx";
import Image from "next/image";
import { forwardRef, memo } from "react";

import type { FragmentType } from "../../../../../../__generated__/gql";
import { graphql, useFragment } from "../../../../../../__generated__/gql";
import type { AccommodationFacilityType } from "../../../../../../__generated__/gql/graphql";
import type { AccommodationNoticeForUser } from "../../../../accommodation/accommodation-notices";
import type { SelectedAccommodationReservation } from "../../reservation-calendar";

import {
  isDateIncludedInReservation,
  canSelectAsCheckoutDate,
} from "./_utils/availability-status";
import { BlockHoverCardWrapper } from "./block-hover-card-wrapper";

export const AccommodationOnAvailabilityCellFragment = graphql(`
  fragment AccommodationOnAvailabilityCell on Accommodation {
    id
    name
    facilityTypes
    notices {
      brief
      detail
      externalUrl
      startDate
      endDate
    }
  }
`);

const AvailabilityFragment = graphql(`
  fragment AvailabilityOnAvailabilityCell on AccommodationAvailability {
    date
    isAvailable
    guaranteedPrice
    block @include(if: $internal) {
      id
      name
      category
      categoryInfo {
        key
        label
        note
        categoryGroup
      }
      isTentative
      note
      createdAt
      updatedAt
      createdByUser {
        name
        email
      }
      updatedByUser {
        name
        email
      }
    }
  }
`);

const AccommodationReservationFragment = graphql(`
  fragment OriginalAccommodationReservationOnAvailabilityCell on AccommodationReservation {
    id
    firstNight
    lastNight
    mutationScope
    accommodation {
      id
    }
  }
`);

type Accommodation = FragmentType<
  typeof AccommodationOnAvailabilityCellFragment
>;

type Availability = FragmentType<typeof AvailabilityFragment>;

type ComponentProps = {
  availabilityData: Availability;
  accommodationData: Accommodation;
  selected: SelectedAccommodationReservation | null;
  originalReservationData: FragmentType<
    typeof AccommodationReservationFragment
  > | null;
  availabilitiesData: Availability[];
  isSampleMode: boolean;
  onClick: (args: {
    date: PlainDate;
    accommodationId: string;
    accommodationName: string;
    accommodationFacilities: AccommodationFacilityType[];
    accommodationNotices: AccommodationNoticeForUser[];
    selected: SelectedAccommodationReservation | null;
  }) => void;
};

const AvailabilityCell = memo(
  forwardRef<HTMLButtonElement, ComponentProps>(
    function AvailabilityCell(props, ref) {
      const {
        selected,
        originalReservationData,
        availabilityData,
        availabilitiesData,
        accommodationData,
        isSampleMode,
        onClick,
        ...restProps
      } = props;
      const availability = useFragment(AvailabilityFragment, availabilityData);
      const availabilities = useFragment(
        AvailabilityFragment,
        availabilitiesData,
      );
      const accommodation = useFragment(
        AccommodationOnAvailabilityCellFragment,
        accommodationData,
      );
      const originalReservation = useFragment(
        AccommodationReservationFragment,
        originalReservationData,
      );

      const date = parsePlainDate(availability.date);
      const accommodationId = accommodation.id;
      const checkoutMode = originalReservation?.mutationScope === "OnlyExit";

      const isSelected = isDateIncludedInReservation(selected, {
        accommodationId,
        date,
      });
      const isJustAfterSelected = isDateIncludedInReservation(selected, {
        accommodationId,
        date: date.subtract({ days: 1 }),
      });

      const reserved = isDateIncludedInReservation(originalReservation, {
        accommodationId,
        date,
      });
      const isJustAfterReserved = isDateIncludedInReservation(
        originalReservation,
        {
          accommodationId,
          date: date.subtract({ days: 1 }),
        },
      );

      let status: "highlight" | "checkout" | "blank" | null = null;
      if (isSelected) {
        status = "highlight";
      } else if (isJustAfterSelected) {
        status = "checkout";
      } else if (
        reserved ||
        (!checkoutMode && availability.isAvailable) ||
        (checkoutMode &&
          (isJustAfterReserved ||
            canSelectAsCheckoutDate(date, originalReservation, availabilities)))
      ) {
        status = "blank";
      } else {
        return null;
      }

      let enabled =
        !isSampleMode &&
        ["highlight", "blank", "cicoFiltered"].includes(status);
      if (checkoutMode) {
        enabled &&=
          PlainDate.compare(date, originalReservation!.firstNight) > 0 &&
          PlainDate.compare(date, getCurrentDate().add({ days: 1 })) >= 0;
      }
      const isBlock =
        availability.block && !["highlight", "checkout"].includes(status);

      return (
        <BlockHoverCardWrapper block={availability.block}>
          <button
            ref={ref}
            {...restProps}
            className={clsx(
              "relative flex h-full w-full items-center justify-center rounded-sm",
              {
                "border-sbeige font-roboto text-oldsblack border bg-white text-[10px] hover:bg-[#f3f3f3]":
                  status === "blank",
                "bg-sorange text-[10px] text-white": status === "highlight",
                "border-sorange text-sorange border bg-white text-[8px] font-bold!":
                  status === "checkout",
                "border-sorange border border-dashed bg-white hover:bg-[#f3f3f3]":
                  isBlock,
              },
            )}
            disabled={!enabled}
            onClick={() => {
              onClick({
                date: date,
                accommodationId: accommodation.id,
                accommodationName: accommodation.name,
                accommodationFacilities: accommodation.facilityTypes,
                accommodationNotices: accommodation.notices,
                selected: selected,
              });
            }}
          >
            {status === "checkout" ? (
              <>
                チェック
                <br />
                アウト
              </>
            ) : typeof availability.guaranteedPrice === "number" &&
              availability.guaranteedPrice > 0 &&
              !isSampleMode ? (
              formatCurrency(availability.guaranteedPrice, {
                excludeSeparator: true,
              })
            ) : isSampleMode ? (
              "⭕️"
            ) : null}
            {status === "highlight" && (
              <div className="absolute top-[3px] right-[3px] h-[6px] w-[6px] rounded-full bg-white"></div>
            )}
            {isBlock && (
              <div className="absolute right-[2px] bottom-[2px] h-[8px] w-[8px]">
                <Image
                  src={"/i/snackbar_icon_alert.svg"}
                  alt="success"
                  width={16}
                  height={16}
                />
              </div>
            )}
          </button>
        </BlockHoverCardWrapper>
      );
    },
  ),
);

export default AvailabilityCell;
